import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

type Props = {
  open: boolean;
  onClose: () => void;
  onCreate: (title: string) => Promise<void>;
  loading?: boolean;
};

export default function CreateLeadFormModal({ open, onClose, onCreate, loading }: Props) {
  const [title, setTitle] = useState('');

  if (!open) return null;

  const submit = async () => {
    if (!title.trim() || loading) return;
    await onCreate(title.trim());
    setTitle('');
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[999999] flex items-end sm:items-center justify-center p-2 sm:p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
      }}
    >
      <div
        className="w-full max-w-md bg-white rounded-t-[16px] sm:rounded-[16px] shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h3 className="text-[16px] font-semibold text-gray-900">Create lead form</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-[10px] active:bg-gray-100"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        <div className="p-4">
          <label className="block text-[12px] font-medium text-gray-500 mb-1.5">Form title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submit();
            }}
            placeholder="e.g. Website contact"
            autoFocus
            className="w-full px-3 py-2.5 border border-gray-200 rounded-[10px] text-[15px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            style={{ minHeight: 44 }}
          />
        </div>
        <div className="p-4 border-t border-gray-100 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-[10px] border border-gray-200 text-[14px] font-semibold text-gray-700"
            style={{ minHeight: 44 }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!title.trim() || loading}
            className="flex-1 px-4 py-2.5 rounded-[10px] bg-blue-600 text-white text-[14px] font-semibold disabled:opacity-50"
            style={{ minHeight: 44 }}
          >
            {loading ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
